import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';
// `StyleIcon` is MUI's canonical "brand mark" glyph — a stylised tag
// with a dot inside, used to convey the identity/branding of a
// product line. It reads better than the generic `Label` tag we
// had before.
import StyleIcon from '@mui/icons-material/Style';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../shared/components/PageHeader';
import { FormDialog } from '../../shared/components/Dialogs/FormDialog';
import { ConfirmDialog } from '../../shared/components/Dialogs/ConfirmDialog';
import { resources } from '../../core/services/resources';
import { extractErrorMessage } from '../../core/services/api';
import type {
  Brand,
  LineOfBiz,
  LineOfBizBrand,
  Organization,
} from '../../core/types/data-models';

/**
 * Hard cap on the number of organizations rendered. The list is sorted
 * alphabetically (case-insensitive), then truncated. Anything beyond
 * shows up via the search filter — so a user looking for "Zoom" still
 * finds it by typing rather than scrolling past 50 rows.
 */
const MAX_ORGS = 50;

/**
 * Hierarchy view: Organizations → Brands → Sub-Brands (recursively).
 *
 * Styled as a single always-open tree (matching the Active Customer
 * Dashboard's LOB tree), not as accordions. Rationale: the tree is
 * navigational — the user is scanning for "where's brand X?" — and
 * collapsing rows hides the very structure they're trying to see.
 * The keyword filter above the tree handles the noise when the tree
 * gets large.
 */

// ---------- Tree model ------------------------------------------------------

interface BrandNode {
  brand: Brand;
  children: BrandNode[];
}

interface OrgGroup {
  org: Organization | null;
  roots: BrandNode[];
}

function buildTree(orgs: Organization[], brands: Brand[]): OrgGroup[] {
  const byParent = new Map<number | null, Brand[]>();
  for (const brand of brands) {
    const key = brand.parentBrandId ?? null;
    const list = byParent.get(key) ?? [];
    list.push(brand);
    byParent.set(key, list);
  }
  const childrenOf = (parentBrandId: number): BrandNode[] =>
    (byParent.get(parentBrandId) ?? [])
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((brand) => ({ brand, children: childrenOf(brand.id) }));

  const rootBrands = (byParent.get(null) ?? [])
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name));

  const groups: OrgGroup[] = orgs.map((org) => ({
    org,
    roots: rootBrands
      .filter((b) => b.organizationId === org.id)
      .map((b) => ({ brand: b, children: childrenOf(b.id) })),
  }));

  // Surface brands without (or with broken) organization linkage under
  // a synthetic group so they don't disappear from the UI.
  const orgIds = new Set(orgs.map((o) => o.id));
  const orphans = rootBrands.filter(
    (b) => b.organizationId == null || !orgIds.has(b.organizationId),
  );
  if (orphans.length > 0) {
    groups.push({
      org: null,
      roots: orphans.map((b) => ({ brand: b, children: childrenOf(b.id) })),
    });
  }
  return groups;
}

/**
 * Keyword filter — case-insensitive substring match. A group or
 * brand is kept if its own name matches OR any descendant matches,
 * so partial matches don't fragment the tree.
 *
 * Empty query → returns the input unchanged (no allocation cost
 * beyond the wrapper objects).
 */
function filterTree(groups: OrgGroup[], query: string): OrgGroup[] {
  const q = query.trim().toLowerCase();
  if (!q) return groups;

  const filterBrand = (node: BrandNode): BrandNode | null => {
    const ownMatch = node.brand.name.toLowerCase().includes(q);
    const filteredChildren = node.children
      .map(filterBrand)
      .filter((c): c is BrandNode => c !== null);
    if (ownMatch || filteredChildren.length > 0) {
      // Preserve all children when the node itself matches; otherwise
      // only keep the children that matched.
      return { brand: node.brand, children: ownMatch ? node.children : filteredChildren };
    }
    return null;
  };

  const out: OrgGroup[] = [];
  for (const group of groups) {
    const ownMatch = group.org ? group.org.name.toLowerCase().includes(q) : false;
    const filteredRoots = group.roots
      .map(filterBrand)
      .filter((b): b is BrandNode => b !== null);
    if (ownMatch || filteredRoots.length > 0) {
      out.push({
        org: group.org,
        roots: ownMatch ? group.roots : filteredRoots,
      });
    }
  }
  return out;
}

// ---------- Page ------------------------------------------------------------

interface OrgFormValues {
  id?: number;
  name: string;
  description: string | null;
  website: string | null;
  logoUrl: string | null;
}
interface BrandFormValues {
  id?: number;
  name: string;
  description: string | null;
  website: string | null;
  logoUrl: string | null;
  organizationId: number | null;
  parentBrandId: number | null;
}

type BrandFormMode =
  | { mode: 'add'; organizationId: number | null; parentBrandId: number | null }
  | { mode: 'edit'; brand: Brand };

const EMPTY_ORG: OrgFormValues = {
  name: '',
  description: null,
  website: null,
  logoUrl: null,
};

export function OrganizationsPage() {
  const queryClient = useQueryClient();

  const orgsQuery = useQuery({
    queryKey: ['organizations'],
    queryFn: () => resources.organizations.list({ pageSize: 500, sort: 'name' }),
  });
  const brandsQuery = useQuery({
    queryKey: ['brands'],
    queryFn: () => resources.brands.list({ pageSize: 1000, sort: 'name' }),
  });
  // LOBs with their brand-junction rows attached. We don't render any
  // LOB data here — we only need the counts of LineOfBizBrand rows per
  // brand id, so a single list call with `?include=lineOfBizBrands` is
  // cheaper than a separate junction-table fetch.
  const lobsQuery = useQuery({
    queryKey: ['lines-of-business', 'with-brands'],
    queryFn: () =>
      resources.linesOfBusiness.list({
        pageSize: 500,
        sort: 'name',
        include: ['lineOfBizBrands'],
      }),
  });

  const orgs = orgsQuery.data?.data ?? [];
  const brands = brandsQuery.data?.data ?? [];

  // Count LOB-attachments per tenant brand from the included junction
  // rows. The junction's `(lineOfBizId, tenantBrandId)` pair is unique
  // by schema, so a raw increment is fine — no need to dedupe.
  //
  // Note: the FK target moved from global `Brand` to `TenantBrand`, so
  // this map is keyed by tenant-brand id, not global-brand id. The
  // page's brand tree still renders global brands; the count alongside
  // each will only line up if the tree starts rendering tenant brands
  // (or we resolve back to commonBrandId via a tenantBrands lookup).
  // Kept compiling here as-is; revisit when this page moves to the
  // tenant-scoped catalog.
  const lobCountByBrandId = useMemo(() => {
    const map = new Map<number, number>();
    const lobs = (lobsQuery.data?.data as LineOfBiz[] | undefined) ?? [];
    for (const lob of lobs) {
      const joins = (lob.lineOfBizBrands ?? []) as LineOfBizBrand[];
      for (const j of joins) {
        map.set(j.tenantBrandId, (map.get(j.tenantBrandId) ?? 0) + 1);
      }
    }
    return map;
  }, [lobsQuery.data]);

  // Toolbar state.
  const [search, setSearch] = useState('');
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  // Per-org expanded state. Default: every loaded org expanded — the
  // user explicitly asked for "user sees the opened tree" as the
  // landing state. Prune stale ids when the org list shifts under us.
  const [expandedOrgs, setExpandedOrgs] = useState<Set<number>>(new Set());
  useEffect(() => {
    setExpandedOrgs((prev) => {
      const next = new Set<number>();
      for (const o of orgs) {
        // First time we see an org → expanded. Already-tracked orgs
        // keep their current state (so toggling persists across
        // re-fetches).
        if (prev.size === 0 || prev.has(o.id)) next.add(o.id);
      }
      return next;
    });
  }, [orgs]);
  const toggleOrg = (id: number) =>
    setExpandedOrgs((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const tree = useMemo(() => buildTree(orgs, brands), [orgs, brands]);
  const filtered = useMemo(() => filterTree(tree, search), [tree, search]);
  // Apply the 50-row cap after filter + alphabetical sort (which
  // `buildTree` already returns in). Surface the omitted count in the
  // UI so users know the cap is active.
  const visible = useMemo(() => filtered.slice(0, MAX_ORGS), [filtered]);
  const truncatedBy = Math.max(filtered.length - visible.length, 0);

  const allOrgsExpanded =
    visible.length > 0 &&
    visible.every((g) => g.org == null || expandedOrgs.has(g.org.id));
  const expandAllOrgs = () => {
    setExpandedOrgs(new Set(orgs.map((o) => o.id)));
  };
  const collapseAllOrgs = () => setExpandedOrgs(new Set());

  // ----- Organization dialog -----------------------------------------
  const [orgDialogOpen, setOrgDialogOpen] = useState(false);
  const [orgEditing, setOrgEditing] = useState<OrgFormValues>(EMPTY_ORG);
  const [orgError, setOrgError] = useState<string | null>(null);

  const openOrgCreate = () => {
    setOrgEditing(EMPTY_ORG);
    setOrgError(null);
    setOrgDialogOpen(true);
  };
  const openOrgEdit = (org: Organization) => {
    setOrgEditing({
      id: org.id,
      name: org.name,
      description: org.description ?? null,
      website: org.website ?? null,
      logoUrl: org.logoUrl ?? null,
    });
    setOrgError(null);
    setOrgDialogOpen(true);
  };

  const orgPayload = (v: OrgFormValues): Partial<Organization> => ({
    name: v.name,
    description: v.description,
    website: v.website,
    logoUrl: v.logoUrl,
  });

  const createOrg = useMutation({
    mutationFn: (v: OrgFormValues) => resources.organizations.create(orgPayload(v)),
    onSuccess: () => {
      setOrgDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
    onError: (e) => setOrgError(extractErrorMessage(e)),
  });
  const updateOrg = useMutation({
    mutationFn: (v: OrgFormValues & { id: number }) =>
      resources.organizations.update(v.id, orgPayload(v)),
    onSuccess: () => {
      setOrgDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
    onError: (e) => setOrgError(extractErrorMessage(e)),
  });
  const removeOrg = useMutation({
    mutationFn: (id: number) => resources.organizations.remove(id),
    onSuccess: () => {
      setConfirmOrgDelete(null);
      // Deleting an organization cascades to its brands at the DB level
      // (Brand.organization is onDelete: Cascade). Invalidate the brand
      // cache too so the tree reflects the removed brands instead of
      // stranding them under "Unassigned brands".
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      queryClient.invalidateQueries({ queryKey: ['brands'] });
      queryClient.invalidateQueries({ queryKey: ['lines-of-business'] });
    },
  });
  const [confirmOrgDelete, setConfirmOrgDelete] = useState<Organization | null>(null);

  const handleOrgSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setOrgError(null);
    if (!orgEditing.name.trim()) {
      setOrgError('Name is required');
      return;
    }
    if (orgEditing.id != null) await updateOrg.mutateAsync({ ...orgEditing, id: orgEditing.id });
    else await createOrg.mutateAsync(orgEditing);
  };

  // ----- Brand dialog ------------------------------------------------
  const [brandDialogOpen, setBrandDialogOpen] = useState(false);
  const [brandMode, setBrandMode] = useState<BrandFormMode | null>(null);
  const [brandValues, setBrandValues] = useState<BrandFormValues>({
    name: '',
    description: null,
    website: null,
    logoUrl: null,
    organizationId: null,
    parentBrandId: null,
  });
  const [brandError, setBrandError] = useState<string | null>(null);

  const openBrandCreateUnderOrg = (orgId: number) => {
    setBrandMode({ mode: 'add', organizationId: orgId, parentBrandId: null });
    setBrandValues({
      name: '',
      description: null,
      website: null,
      logoUrl: null,
      organizationId: orgId,
      parentBrandId: null,
    });
    setBrandError(null);
    setBrandDialogOpen(true);
  };
  const openBrandCreateUnderBrand = (parent: Brand) => {
    setBrandMode({ mode: 'add', organizationId: parent.organizationId, parentBrandId: parent.id });
    setBrandValues({
      name: '',
      description: null,
      website: null,
      logoUrl: null,
      organizationId: parent.organizationId,
      parentBrandId: parent.id,
    });
    setBrandError(null);
    setBrandDialogOpen(true);
  };
  const openBrandEdit = (brand: Brand) => {
    setBrandMode({ mode: 'edit', brand });
    setBrandValues({
      id: brand.id,
      name: brand.name,
      description: brand.description ?? null,
      website: brand.website ?? null,
      logoUrl: brand.logoUrl ?? null,
      organizationId: brand.organizationId,
      parentBrandId: brand.parentBrandId,
    });
    setBrandError(null);
    setBrandDialogOpen(true);
  };

  const brandPayload = (v: BrandFormValues): Partial<Brand> => ({
    name: v.name,
    description: v.description,
    website: v.website,
    logoUrl: v.logoUrl,
    organizationId: v.organizationId ?? undefined,
    parentBrandId: v.parentBrandId,
  });

  const createBrand = useMutation({
    mutationFn: (v: BrandFormValues) => resources.brands.create(brandPayload(v)),
    onSuccess: () => {
      setBrandDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ['brands'] });
    },
    onError: (e) => setBrandError(extractErrorMessage(e)),
  });
  const updateBrand = useMutation({
    mutationFn: (v: BrandFormValues & { id: number }) =>
      resources.brands.update(v.id, brandPayload(v)),
    onSuccess: () => {
      setBrandDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ['brands'] });
    },
    onError: (e) => setBrandError(extractErrorMessage(e)),
  });
  const removeBrand = useMutation({
    mutationFn: (id: number) => resources.brands.remove(id),
    onSuccess: () => {
      setConfirmBrandDelete(null);
      queryClient.invalidateQueries({ queryKey: ['brands'] });
    },
  });
  const [confirmBrandDelete, setConfirmBrandDelete] = useState<Brand | null>(null);

  const handleBrandSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBrandError(null);
    if (!brandValues.name.trim()) {
      setBrandError('Name is required');
      return;
    }
    if (brandValues.id != null) {
      await updateBrand.mutateAsync({ ...brandValues, id: brandValues.id });
    } else {
      await createBrand.mutateAsync(brandValues);
    }
  };

  const isLoading = orgsQuery.isLoading || brandsQuery.isLoading;
  const error = orgsQuery.error ?? brandsQuery.error;

  return (
    <Box>
      <PageHeader
        title="Organizations"
        description="Top-level organizations and the brand hierarchy they own."
        actions={
          // `+ New organization` only matters in edit mode. Hiding it in
          // view mode keeps the toolbar focused on browsing.
          mode === 'edit' ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openOrgCreate}>
              New organization
            </Button>
          ) : null
        }
      />

      {/* Toolbar: search input on the left, expand-all / collapse-all
          toggle and view/edit mode toggle on the right. */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Filter organizations and brands…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Tooltip title={allOrgsExpanded ? 'Collapse all organizations' : 'Expand all organizations'}>
          <span>
            <IconButton
              onClick={allOrgsExpanded ? collapseAllOrgs : expandAllOrgs}
              disabled={orgs.length === 0}
              aria-label={allOrgsExpanded ? 'collapse all' : 'expand all'}
            >
              {allOrgsExpanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
            </IconButton>
          </span>
        </Tooltip>
        {/* View / Edit mode. View mode hides destructive + create
            affordances so casual browsing can't accidentally mutate. */}
        <ToggleButtonGroup
          size="small"
          exclusive
          value={mode}
          onChange={(_, next) => {
            if (next === 'view' || next === 'edit') setMode(next);
          }}
          aria-label="page mode"
        >
          <ToggleButton value="view" aria-label="view mode">
            <Tooltip title="View mode">
              <VisibilityIcon fontSize="small" />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="edit" aria-label="edit mode">
            <Tooltip title="Edit mode">
              <EditNoteIcon fontSize="small" />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{extractErrorMessage(error)}</Alert>
      )}

      {!isLoading && tree.length === 0 ? (
        <Alert severity="info">
          No organizations yet. Click <strong>New organization</strong> to add one.
        </Alert>
      ) : !isLoading && filtered.length === 0 ? (
        <Alert severity="info">No matches for &ldquo;{search}&rdquo;.</Alert>
      ) : (
        <>
          <Stack spacing={0.25} sx={{ pl: 0.5 }}>
            {visible.map((group, idx) => (
              <OrgRows
                key={group.org?.id ?? `orphans-${idx}`}
                org={group.org}
                roots={group.roots}
                expanded={group.org ? expandedOrgs.has(group.org.id) : true}
                onToggle={group.org ? () => toggleOrg(group.org!.id) : undefined}
                mode={mode}
                lobCountByBrandId={lobCountByBrandId}
                onAddBrand={(orgId) => openBrandCreateUnderOrg(orgId)}
                onAddSubBrand={openBrandCreateUnderBrand}
                onEditOrg={openOrgEdit}
                onDeleteOrg={(o) => setConfirmOrgDelete(o)}
                onEditBrand={openBrandEdit}
                onDeleteBrand={(b) => setConfirmBrandDelete(b)}
              />
            ))}
          </Stack>
          {truncatedBy > 0 && (
            // Hard-cap hint. We don't paginate — when the user wants to
            // see more orgs, the right move is to filter.
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 1.5, pl: 0.5 }}
            >
              Showing the first {visible.length} of {filtered.length} organizations.
              Refine your search to narrow the list.
            </Typography>
          )}
        </>
      )}

      {/* Organization form dialog */}
      <FormDialog
        open={orgDialogOpen}
        title={orgEditing.id != null ? 'Edit organization' : 'New organization'}
        submitLabel={orgEditing.id != null ? 'Save' : 'Create'}
        loading={createOrg.isPending || updateOrg.isPending}
        onClose={() => {
          if (createOrg.isPending || updateOrg.isPending) return;
          setOrgDialogOpen(false);
        }}
        onSubmit={handleOrgSubmit}
      >
        {orgError && <Alert severity="error">{orgError}</Alert>}
        <Stack spacing={2}>
          <TextField
            label="Name"
            required
            autoFocus
            value={orgEditing.name}
            onChange={(e) => setOrgEditing({ ...orgEditing, name: e.target.value })}
          />
          <TextField
            label="Website"
            placeholder="https://example.com"
            value={orgEditing.website ?? ''}
            onChange={(e) =>
              setOrgEditing({
                ...orgEditing,
                website: e.target.value === '' ? null : e.target.value,
              })
            }
          />
          <TextField
            label="Logo URL"
            placeholder="https://cdn.example.com/logo.png"
            value={orgEditing.logoUrl ?? ''}
            onChange={(e) =>
              setOrgEditing({
                ...orgEditing,
                logoUrl: e.target.value === '' ? null : e.target.value,
              })
            }
          />
          <TextField
            label="Description"
            multiline
            minRows={2}
            value={orgEditing.description ?? ''}
            onChange={(e) =>
              setOrgEditing({
                ...orgEditing,
                description: e.target.value === '' ? null : e.target.value,
              })
            }
          />
        </Stack>
      </FormDialog>

      {/* Brand form dialog */}
      <FormDialog
        open={brandDialogOpen}
        title={brandTitle(brandMode, orgs, brands)}
        submitLabel={brandMode?.mode === 'edit' ? 'Save' : 'Create'}
        loading={createBrand.isPending || updateBrand.isPending}
        onClose={() => {
          if (createBrand.isPending || updateBrand.isPending) return;
          setBrandDialogOpen(false);
        }}
        onSubmit={handleBrandSubmit}
      >
        {brandError && <Alert severity="error">{brandError}</Alert>}
        <Stack spacing={2}>
          <TextField
            label="Name"
            required
            autoFocus
            value={brandValues.name}
            onChange={(e) => setBrandValues({ ...brandValues, name: e.target.value })}
          />
          <TextField
            label="Website"
            placeholder="https://example.com"
            value={brandValues.website ?? ''}
            onChange={(e) =>
              setBrandValues({
                ...brandValues,
                website: e.target.value === '' ? null : e.target.value,
              })
            }
          />
          <TextField
            label="Logo URL"
            placeholder="https://cdn.example.com/logo.png"
            value={brandValues.logoUrl ?? ''}
            onChange={(e) =>
              setBrandValues({
                ...brandValues,
                logoUrl: e.target.value === '' ? null : e.target.value,
              })
            }
          />
          <TextField
            label="Description"
            multiline
            minRows={2}
            value={brandValues.description ?? ''}
            onChange={(e) =>
              setBrandValues({
                ...brandValues,
                description: e.target.value === '' ? null : e.target.value,
              })
            }
          />
          <Typography variant="caption" color="text.secondary">
            {brandContextLabel(brandValues, orgs, brands)}
          </Typography>
        </Stack>
      </FormDialog>

      {/* Delete confirmations. */}
      <ConfirmDialog
        open={confirmOrgDelete != null}
        title="Delete organization?"
        message={confirmOrgDelete ? orgDeleteMessage(confirmOrgDelete, brands) : ''}
        destructive
        confirmLabel={removeOrg.isPending ? 'Deleting…' : 'Delete'}
        loading={removeOrg.isPending}
        onConfirm={() => {
          if (confirmOrgDelete) removeOrg.mutate(confirmOrgDelete.id);
        }}
        onClose={() => {
          if (removeOrg.isPending) return;
          setConfirmOrgDelete(null);
        }}
      />
      <ConfirmDialog
        open={confirmBrandDelete != null}
        title="Delete brand?"
        message={confirmBrandDelete ? `Delete "${confirmBrandDelete.name}"? This can't be undone.` : ''}
        destructive
        confirmLabel={removeBrand.isPending ? 'Deleting…' : 'Delete'}
        loading={removeBrand.isPending}
        onConfirm={() => {
          if (confirmBrandDelete) removeBrand.mutate(confirmBrandDelete.id);
        }}
        onClose={() => {
          if (removeBrand.isPending) return;
          setConfirmBrandDelete(null);
        }}
      />
    </Box>
  );
}

// ---------- Sub-components --------------------------------------------------

interface OrgRowsProps {
  org: Organization | null;
  roots: BrandNode[];
  expanded: boolean;
  /** Toggle handler — undefined for the synthetic "Unassigned" group, which is always expanded. */
  onToggle?: () => void;
  mode: 'view' | 'edit';
  lobCountByBrandId: Map<number, number>;
  onAddBrand: (orgId: number) => void;
  onAddSubBrand: (parent: Brand) => void;
  onEditOrg: (org: Organization) => void;
  onDeleteOrg: (org: Organization) => void;
  onEditBrand: (brand: Brand) => void;
  onDeleteBrand: (brand: Brand) => void;
}

/**
 * Renders one organization header row + its descendants. Org rows are
 * collapsible via the leading chevron; brand sub-trees are always
 * visible when the org is expanded.
 */
function OrgRows({
  org,
  roots,
  expanded,
  onToggle,
  mode,
  lobCountByBrandId,
  onAddBrand,
  onAddSubBrand,
  onEditOrg,
  onDeleteOrg,
  onEditBrand,
  onDeleteBrand,
}: OrgRowsProps) {
  const brandCount = countBrands(roots);
  return (
    <Box>
      <TreeRow
        depth={0}
        // Chevron rotates in place to signal expand state. Click on
        // the chevron toggles only the org's children.
        icon={
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {onToggle ? (
              <IconButton size="small" onClick={onToggle} aria-label={expanded ? 'collapse' : 'expand'}>
                <ExpandMoreIcon
                  fontSize="small"
                  sx={{
                    transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                    transition: (t) => t.transitions.create('transform'),
                  }}
                />
              </IconButton>
            ) : (
              // Synthetic / no-org row: keep the same width so brand
              // rows stay aligned across the table.
              <Box sx={{ width: 28 }} />
            )}
            {org?.logoUrl ? (
              <Box
                component="img"
                src={org.logoUrl}
                alt=""
                sx={{ height: 18, width: 18, objectFit: 'contain' }}
              />
            ) : (
              <BusinessIcon fontSize="small" sx={{ color: 'primary.main' }} />
            )}
          </Stack>
        }
        primary={
          <Tooltip
            title={org?.description ?? ''}
            disableHoverListener={!org?.description}
            disableFocusListener={!org?.description}
            placement="top"
          >
            <Typography variant="body2" fontWeight={700} sx={{ color: 'primary.main' }} noWrap>
              {org?.name ?? 'Unassigned brands'}
              {org?.website && (
                <Box
                  component="a"
                  href={org.website}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    ml: 1,
                    color: 'text.secondary',
                    fontWeight: 400,
                    fontSize: 12,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {org.website}
                </Box>
              )}
            </Typography>
          </Tooltip>
        }
        // IDs are operational details — only useful while editing,
        // so we keep them out of the way in view mode.
        idChip={mode === 'edit' && org ? `#${org.id}` : undefined}
        trailing={
          <Stack direction="row" alignItems="center" spacing={0.25}>
            {brandCount > 0 && (
              <Chip
                size="small"
                label={`${brandCount} ${brandCount === 1 ? 'brand' : 'brands'}`}
                sx={{ height: 18, mr: 0.5 }}
              />
            )}
            {mode === 'edit' && org && (
              <>
                <Tooltip title="Add brand">
                  <IconButton size="small" onClick={() => onAddBrand(org.id)} aria-label="add brand">
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => onEditOrg(org)} aria-label="edit organization">
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onDeleteOrg(org)}
                    aria-label="delete organization"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Stack>
        }
      />
      {expanded &&
        roots.map((node) => (
          <BrandRows
            key={node.brand.id}
            node={node}
            depth={1}
            mode={mode}
            lobCountByBrandId={lobCountByBrandId}
            onAddSubBrand={onAddSubBrand}
            onEditBrand={onEditBrand}
            onDeleteBrand={onDeleteBrand}
          />
        ))}
    </Box>
  );
}

interface BrandRowsProps {
  node: BrandNode;
  depth: number;
  mode: 'view' | 'edit';
  lobCountByBrandId: Map<number, number>;
  onAddSubBrand: (parent: Brand) => void;
  onEditBrand: (brand: Brand) => void;
  onDeleteBrand: (brand: Brand) => void;
}

function BrandRows({
  node,
  depth,
  mode,
  lobCountByBrandId,
  onAddSubBrand,
  onEditBrand,
  onDeleteBrand,
}: BrandRowsProps) {
  // Sub-brand count = total descendants of this brand (not just direct
  // children) so a deeply-nested family still shows its size at a glance.
  const subBrandCount = countBrands(node.children);
  const lobCount = lobCountByBrandId.get(node.brand.id) ?? 0;
  return (
    <Box>
      <TreeRow
        depth={depth}
        icon={
          node.brand.logoUrl ? (
            <Box
              component="img"
              src={node.brand.logoUrl}
              alt=""
              sx={{ height: 18, width: 18, objectFit: 'contain' }}
            />
          ) : (
            <StyleIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          )
        }
        primary={
          <Tooltip
            title={node.brand.description ?? ''}
            disableHoverListener={!node.brand.description}
            disableFocusListener={!node.brand.description}
            placement="top"
          >
            <Typography
              variant="body2"
              fontWeight={node.children.length > 0 ? 600 : 400}
              noWrap
            >
              {node.brand.name}
              {node.brand.website && (
                <Box
                  component="a"
                  href={node.brand.website}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    ml: 1,
                    color: 'text.secondary',
                    fontWeight: 400,
                    fontSize: 12,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {node.brand.website}
                </Box>
              )}
            </Typography>
          </Tooltip>
        }
        idChip={mode === 'edit' ? `#${node.brand.id}` : undefined}
        trailing={
          <Stack direction="row" alignItems="center" spacing={0.25}>
            {subBrandCount > 0 && (
              <Tooltip title="Total descendant brands (all depths)">
                <Chip
                  size="small"
                  label={`${subBrandCount} sub-${subBrandCount === 1 ? 'brand' : 'brands'}`}
                  sx={{ height: 18, mr: 0.25 }}
                />
              </Tooltip>
            )}
            {lobCount > 0 && (
              <Tooltip title="Lines of business this brand is attached to">
                <Chip
                  size="small"
                  color="primary"
                  variant="outlined"
                  label={`${lobCount} LOB${lobCount === 1 ? '' : 's'}`}
                  sx={{ height: 18, mr: 0.5 }}
                />
              </Tooltip>
            )}
            {mode === 'edit' && (
              <>
                <Tooltip title="Add sub-brand">
                  <IconButton
                    size="small"
                    onClick={() => onAddSubBrand(node.brand)}
                    aria-label="add sub-brand"
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => onEditBrand(node.brand)} aria-label="edit brand">
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onDeleteBrand(node.brand)}
                    aria-label="delete brand"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Stack>
        }
      />
      {node.children.map((child) => (
        <BrandRows
          key={child.brand.id}
          node={child}
          depth={depth + 1}
          mode={mode}
          lobCountByBrandId={lobCountByBrandId}
          onAddSubBrand={onAddSubBrand}
          onEditBrand={onEditBrand}
          onDeleteBrand={onDeleteBrand}
        />
      ))}
    </Box>
  );
}

/**
 * Single row template — keeps every row in the tree visually
 * identical (icon + name + actions). Indent comes from `depth`,
 * matching the dashboard tree's 2-units-per-level scale.
 */
function TreeRow({
  depth,
  icon,
  primary,
  idChip,
  trailing,
}: {
  depth: number;
  icon: React.ReactNode;
  primary: React.ReactNode;
  idChip?: string;
  trailing?: React.ReactNode;
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        pl: depth * 2,
        pr: 1,
        py: 0.5,
        borderRadius: 1,
        '&:hover': { backgroundColor: 'action.hover' },
      }}
    >
      {icon}
      {primary}
      {idChip && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontFamily: 'monospace' }}
        >
          {idChip}
        </Typography>
      )}
      <Box sx={{ flexGrow: 1 }} />
      {trailing}
    </Stack>
  );
}

// ---------- Helpers ---------------------------------------------------------

/**
 * Confirm-dialog copy for deleting an organization. Deleting an org
 * cascades to every brand it owns, so we surface that count up front —
 * the delete is destructive beyond just the org row.
 */
function orgDeleteMessage(org: Organization, brands: Brand[]): string {
  const owned = brands.filter((b) => b.organizationId === org.id).length;
  if (owned === 0) {
    return `Delete "${org.name}"? This can't be undone.`;
  }
  return `Delete "${org.name}" and its ${owned} ${
    owned === 1 ? 'brand' : 'brands'
  }? All brands under this organization will be deleted too. This can't be undone.`;
}

function countBrands(roots: BrandNode[]): number {
  let n = 0;
  const walk = (nodes: BrandNode[]) => {
    for (const node of nodes) {
      n += 1;
      walk(node.children);
    }
  };
  walk(roots);
  return n;
}

function brandTitle(
  mode: BrandFormMode | null,
  orgs: Organization[],
  brands: Brand[],
): string {
  if (!mode) return 'Brand';
  if (mode.mode === 'edit') return `Edit ${mode.brand.name}`;
  if (mode.parentBrandId != null) {
    const parent = brands.find((b) => b.id === mode.parentBrandId);
    return parent ? `Add sub-brand under ${parent.name}` : 'Add sub-brand';
  }
  if (mode.organizationId != null) {
    const org = orgs.find((o) => o.id === mode.organizationId);
    return org ? `Add brand under ${org.name}` : 'Add brand';
  }
  return 'Add brand';
}

function brandContextLabel(
  values: BrandFormValues,
  orgs: Organization[],
  brands: Brand[],
): string {
  const org = values.organizationId != null
    ? orgs.find((o) => o.id === values.organizationId)?.name ?? `#${values.organizationId}`
    : null;
  const parent = values.parentBrandId != null
    ? brands.find((b) => b.id === values.parentBrandId)?.name ?? `#${values.parentBrandId}`
    : null;
  if (parent && org) return `Under ${org} → ${parent}.`;
  if (parent) return `Under ${parent}.`;
  if (org) return `Top-level brand of ${org}.`;
  return 'Top-level brand — no organization or parent.';
}
